/**
 * The public marketing site: the pages anyone can read without signing in,
 * and the only pages that load the institution's tag manager.
 *
 * Everything else — the dashboard, sign-in, password reset, placement tests,
 * certificate verification, OAuth — is the application. Its URLs carry
 * tokens, verification codes and record ids, and its pages show student
 * data, so no third-party tag runs there. `next.config.ts` builds each
 * side's Content-Security-Policy from this same definition, and
 * `PublicTagManager` decides where to load from it, so the two cannot drift.
 */

/** Path prefixes of the public site, besides the home page itself. */
export const PUBLIC_SITE_SECTIONS = ['/courses', '/articles', '/privacy-policy'] as const;

/** Whether a pathname belongs to the public marketing site. */
export function isPublicSitePath(pathname: string): boolean {
  if (pathname === '/') return true;
  return PUBLIC_SITE_SECTIONS.some(
    (section) => pathname === section || pathname.startsWith(`${section}/`)
  );
}

/**
 * `next.config.ts` header sources for the public site, and one for
 * everything else. Disjoint on purpose: a route that matched two entries
 * would receive two CSP headers, and browsers enforce their intersection.
 */
export const PUBLIC_SITE_HEADER_SOURCES = [
  '/',
  // `:path*` also matches the section's own page, so `/courses` is listed once.
  ...PUBLIC_SITE_SECTIONS.map((section) => `${section}/:path*`),
];

const sectionNames = PUBLIC_SITE_SECTIONS.map((section) => section.slice(1));
export const APPLICATION_HEADER_SOURCE = `/:path((?!$|(?:${sectionNames.join('|')})(?:/|$)).*)`;

/**
 * Go from the public site into the application with a full page load. A
 * client-side navigation would carry a loaded tag manager into the app, and
 * Analytics reports client-side navigations as page views.
 */
export function leavePublicSite(path: string): void {
  browserNavigation.assign(path);
}

/** Full page loads, behind one seam so tests can observe them. */
export const browserNavigation = {
  assign: (path: string) => window.location.assign(path),
  reload: () => window.location.reload(),
};
